<template>
  <BasePopup
    ref="basePopup"
    :title="newStyle20321B ? 'Save to:' : 'Add to...'"
    class="add-to-list"
    :class="{ 'add-to-list--20321B': newStyle20321B }"
    :compact="!newStyle20321B"
    :menu="newStyle20321B"
    :anchor="anchor"
    @hide="view.clearState()"
  >
    <!--
      In the style of variant B for #20321 (new UI for Save-for-Later), show
      the New List button at the top right.
    -->
    <template v-if="newStyle20321B" #headerAction>
      <AppButton
        class="add-to-list__btn add-to-list__btn--new"
        icon="iadd"
        :small="true"
        :outlined="true"
        @click="showCreateList"
        >New List</AppButton
      >
    </template>

    <v-wait for="Error Loading Lists">
      <template v-slot:waiting>
        <LoadingError class="add-to-list__error" />
      </template>
    </v-wait>

    <v-wait for="Loading Lists">
      <template v-slot:waiting>
        <CircleSpinner
          class="add-to-list__spinner"
          color="#ffc906"
          :size="36"
        />
      </template>

      <div class="add-to-list__list">
        <template v-for="list in view.lists" :key="list.id">
          <!-- Show a ToggleButton (checkbox) for each list. -->
          <ToggleButton
            type="checkbox"
            :variant="newStyle20321B ? 'menu' : undefined"
            class="add-to-list__input"
            :value="view.listsState[list.url_slug]"
            @update:value="
              (updatedValue) =>
                newStyle20321B
                  ? view.toggleDestination(list, updatedValue)
                  : view.toggleList(list, updatedValue)
            "
          >
            {{ list.title }}
          </ToggleButton>
          <!--
            In the 20321B style, show Favorites after Want to Read. We manually
            add it because it is not a custom list, so it is not in view.lists.
          -->
          <ToggleButton
            v-if="newStyle20321B && isWantToRead(list)"
            type="checkbox"
            variant="menu"
            class="add-to-list__input"
            :value="view.doc.user.is_favorite"
            @update:value="
              (checked) => view.toggleDestination('favorites', checked)
            "
          >
            Favorites
          </ToggleButton>
        </template>
      </div>

      <div class="add-to-list__controls">
        <AppButton
          v-if="!newStyle20321B"
          class="add-to-list__btn add-to-list__btn--new"
          icon="iadd"
          :outlined="true"
          @click="showCreateList"
          >New List</AppButton
        >
        <AppButton
          class="add-to-list__btn add-to-list__btn--done"
          @click="basePopup?.hide()"
          >Done</AppButton
        >
      </div>
    </v-wait>
  </BasePopup>

  <CustomListForm ref="customListForm" :view="formView" />
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import type { Raw } from 'vue'

import {
  isWantToRead,
  type DocCustomListsView,
} from '@/models/doc.custom.lists'

import BasePopup from '@/components/ui/BasePopup.vue'
import CircleSpinner from '@/components/ui/CircleSpinner.vue'
import LoadingError from '@/components/ui/LoadingError.vue'
import ToggleButton from '@/components/ui/ToggleButton.vue'
import CustomListForm from '../custom.lists/CustomListForm.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import { CreateCustomListForm } from '@/models/custom.lists'

const props = defineProps<{
  view: DocCustomListsView
  /**
   * Use the Save destination menu from #20321 variant B: the redesigned
   * menu, Favorites in the list of lists, and each change applied through
   * `toggleDestination()` instead of `toggleList()`.
   */
  newStyle20321B?: boolean
  /**
   * On desktop, show the popup next to this element.
   */
  anchor?: HTMLElement | null
}>()

const basePopup = ref<InstanceType<typeof BasePopup> | null>(null)
const customListForm = ref<InstanceType<typeof CustomListForm> | null>(null)

const formView = ref<Raw<CreateCustomListForm>>(
  new CreateCustomListForm(props.view),
)

// The parent replaces `view` when its doc changes (e.g. if you navigate to a
// different guide while you're in the reading view). Recreate the custom list
// form so that creating a custom list here will get that new doc.
watch(
  () => props.view,
  (view) => {
    formView.value = new CreateCustomListForm(view)
  },
)

async function show(): Promise<void> {
  if (!basePopup.value) {
    return
  }

  if (props.newStyle20321B) {
    // Wait for the list data to load so that the popup opens at its final
    // size. Once it does, we use that size to position it next to `anchor`.
    await props.view.getLists()
    basePopup.value?.show()
    return
  }

  basePopup.value.show()
  await props.view.getLists()
}

/**
 * Show the "Create New List" popup and hide this one.
 */
function showCreateList(): void {
  basePopup.value?.hide()
  customListForm.value?.show()
}

onBeforeUnmount(async () => {
  await props.view.clearState()
})

defineExpose({
  show,
})
</script>

<style lang="scss" scoped>
.add-to-list {
  --list-item-hover-bg: #{$c-light-grey};

  &__controls {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
  }

  &__btn {
    flex: 1;
  }
}

.night-mode .add-to-list {
  --list-item-hover-bg: #{$c-dark-2};
}

// Styles for the new "Save to" menu UI (#20321, variant B).
.add-to-list--20321B {
  .add-to-list {
    &__btn--new {
      flex: none;
      width: auto;

      .night-mode & {
        border-color: $c-dark-7;
        background: transparent;
        color: $c-dark-4;

        @include hover {
          border-color: $c-dark-4;
          background: rgba($c-white, 0.05);
        }
      }
    }

    &__controls {
      margin-top: 16px;

      @media (max-width: $w-medium-s) {
        margin-inline: -24px;
        padding: 24px 24px 0;
        border-top: 1px solid $c-black-17;

        .night-mode & {
          border-top-color: $c-dark-5;
        }
      }
    }

    &__btn--done {
      @media (min-width: $w-medium-b) {
        flex: none;
        width: auto;
        padding-inline: 32px;
      }
    }
  }

  // Extend the list rows 8px on each side, so that when you hover over the
  // row, the highlight will surround the checkbox (ToggleButton).
  :deep(.add-to-list__list) {
    gap: 0;
    padding-block: 0;
    margin-inline: -8px;
  }

  :deep(.add-to-list__input) {
    position: relative;
    padding: 15px 8px;
    color: $c-black-15;

    .night-mode & {
      color: $c-dark-4;
    }

    // The divider between each row is only as long as the content, and doesn't
    // extend the 8px on each side that we add above.
    &:not(:last-child):after {
      content: '';
      position: absolute;
      right: 8px;
      bottom: 0;
      left: 8px;
      height: 1px;
      background: $c-black-17;

      .night-mode & {
        background: $c-dark-5;
      }
    }
  }
}

:deep(.add-to-list__spinner) {
  margin-top: 16px;
}

:deep(.add-to-list__list) {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: auto;
  padding-block: 16px;
  margin-left: -4px;
  min-height: 60px;
  max-height: 300px;

  &::-webkit-scrollbar-track-piece {
    margin-top: 16px;
  }
}

:deep(.add-to-list__input) {
  padding: 6px;
  border-radius: 4px;
  margin-block: auto;
  @include hover {
    background: var(--list-item-hover-bg);
  }
}

:deep(.add-to-list__btn) {
  width: 100%;
}

:deep(.add-to-list__btn .iconfont) {
  line-height: 1;
  margin-right: 4px;
}
</style>
